/**
 * Admin shell per-section mini-tours (EPIC #1321, part 5/9).
 *
 * One short deep-dive per admin nav group (single-source ``adminNav.ts``),
 * phrased around the office admin's everyday job -- launched opt-in from the
 * admin Training surface (``/admin/training``, #1328) or the first-visit nudge
 * (8/9), never forced. Registered in the central registry (1/9) with a single
 * spread so the Guided Tours launcher (#1328) lists them automatically; each
 * runs STANDALONE from any starting page (the engine navigates to the section
 * first), is individually skippable, records completion to TourProgress, and
 * stays inside the admin-layout shell (never bounces into the agent CRM).
 *
 * Conventions (mirror agentSectionTours.ts, the 4/9 template):
 *  - Copy rules (D4-ONB-011 / D4-WLB): agency-neutral via the ``{agency}`` token
 *    (never a hardcoded agency/vendor name -- the D4-WLB-008 grep guard stays
 *    green); plain professional language for non-technical admins; NO emojis.
 *  - Gating (D4-ONB-004): a step declares the same ``module`` gate the admin
 *    sidebar's ``itemVisible`` uses (``adminNav.ts``), so a step silently
 *    disappears when its surface is off -- Commission / Leaderboard / Payslips /
 *    Commission Authorizations gate on ``module: "financial"``, FICA / STR on
 *    ``module: "fica"``. (Admins hold every permission flag, so -- exactly like
 *    the admin sidebar -- these tours gate on MODULE only, never permFlag.) A
 *    step whose target is momentarily absent is silently advanced past by the
 *    engine (TARGET_NOT_FOUND), so each tour opens with a ``center`` narrative
 *    step that always renders.
 *  - Targets are stable ``data-tour="..."`` anchors. Sidebar items resolve to
 *    the auto-generated ``nav-<route>`` anchor (NavGroupSection) and declare the
 *    admin ``navGroup`` to expand (and, below lg:, the mobile drawer to open)
 *    before highlighting; in-page anchors live on the section's own admin page.
 *
 * REQs: D4-ONB-008 (admin mini-tours), D4-ONB-011 (agency-neutral copy).
 */
import type { TourDefinition } from "./types";

/**
 * Dashboards -- the company-wide overview group: Overview, Monthly Summary,
 * Deal / Attorney Pipeline, Commission, Leaderboard, Sales Targets, Market
 * Share. One tour over the group, not one per chart. The two money dashboards
 * (Commission, Leaderboard) gate on the ``financial`` module so they vanish when
 * it is off.
 */
export const ADMIN_DASHBOARDS_TOUR: TourDefinition = {
  id: "admin-dashboards",
  title: "Dashboards tour",
  // Audience gate (issue #1347): admin-shell tour -- offered only to admin-role
  // users, never a plain agent whose ungated first step would otherwise pass.
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/dashboard",
      target: "center",
      section: "Welcome",
      title: "Your company dashboards",
      body: "Dashboards give you the whole agency at a glance -- your pipeline, commission, targets and market share, all company-wide. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "overview",
      route: "/admin/dashboard",
      target: "admin-dashboard-home",
      section: "Overview",
      title: "Your overview",
      body: "The Overview is your home page -- a company-wide summary with quick links into every dashboard, from the deal and attorney pipelines and market share through to pipeline cash-flow and bond analytics. Start here each morning.",
      placement: "bottom",
    },
    {
      id: "monthly-summary",
      route: "/admin/dashboard",
      target: "nav-/admin/monthly-deals",
      section: "Monthly Summary",
      navGroup: "dashboards",
      title: "Monthly Summary",
      body: "Monthly Summary rolls up the new, lease and registered deals for a month so you can see how the agency performed at a glance.",
      placement: "right",
    },
    {
      id: "deal-pipeline",
      route: "/admin/dashboard",
      target: "nav-/admin/deal-pipeline",
      section: "Deal Pipeline",
      navGroup: "dashboards",
      title: "Deal Pipeline",
      body: "The Deal Pipeline tracks every live deal by stage and value across the whole agency, so nothing stalls unnoticed.",
      placement: "right",
    },
    {
      id: "attorney-pipeline",
      route: "/admin/dashboard",
      target: "nav-/admin/attorney-pipeline",
      section: "Attorney Pipeline",
      navGroup: "dashboards",
      title: "Attorney Pipeline",
      body: "The Attorney Pipeline groups your deals by conveyancer so you can see who is sitting on transfers that should be registering.",
      placement: "right",
    },
    {
      id: "commission",
      route: "/admin/dashboard",
      target: "nav-/admin/commission",
      section: "Commission",
      navGroup: "dashboards",
      module: "financial",
      title: "Commission",
      body: "The Commission dashboard shows what the agency has earned, what has been paid and what is still outstanding, agent by agent. Just below it sit the monthly Commission Sheet and Authorizations, your commission sign-off inbox: a split is paid only after a finance approval and a separate super-user release, by two different people.",
      placement: "right",
    },
    {
      id: "leaderboard",
      route: "/admin/dashboard",
      target: "nav-/admin/leaderboard",
      section: "Leaderboard",
      navGroup: "dashboards",
      module: "financial",
      title: "Leaderboard",
      body: "The Leaderboard ranks your agents by registered turnover, so you always know who is leading and who needs a nudge.",
      placement: "right",
    },
    {
      id: "sales-targets",
      route: "/admin/dashboard",
      target: "nav-/admin/sales-targets",
      section: "Sales Targets",
      navGroup: "dashboards",
      title: "Sales Targets",
      body: "Sales Targets tracks each agent and the agency as a whole against the year's targets, so you can see who is on track.",
      placement: "right",
    },
    {
      id: "market-share",
      route: "/admin/dashboard",
      target: "nav-/admin/market-share",
      section: "Market Share",
      navGroup: "dashboards",
      title: "Market Share",
      body: "Market Share shows your share of registered deals by area, so you can see where the agency is strong and where to push. Beyond these, activity and agent-usage reports one group down round out the picture. That is your dashboards -- explore at your own pace.",
      placement: "right",
    },
  ],
};

/**
 * User Management -- invite agents and office staff, set their role and the
 * permission preset that decides what each person can see and do. Surfaces the
 * outbound-email suppression banner (#1084) so the admin understands when an
 * invite email will not actually be delivered.
 */
export const ADMIN_USERS_TOUR: TourDefinition = {
  id: "admin-users",
  title: "User Management tour",
  // Audience gate (issue #1347): admin-shell tour -- offered only to admin-role
  // users, never a plain agent whose ungated first step would otherwise pass.
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/users",
      target: "page-admin-users",
      section: "Welcome",
      title: "Your people",
      body: "User Management is where you invite your agents and office staff and decide what each of them can see and do. This is usually your first job on a new {agency} workspace. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "email-suppression",
      route: "/admin/users",
      target: "admin-users-email-suppression",
      section: "Email status",
      title: "Outbound email status",
      body: "Inviting someone here sends them an email with a link to set up their account. If you see an amber notice here, outbound email is currently switched off for testing -- an invite is still created but the email is not actually sent, and you are given a one-time setup link to copy and pass on yourself instead. Once your workspace is live, invites and other automated emails are delivered by real email.",
      placement: "bottom",
    },
    {
      // ACTION step (issue #1342): beacon on the Add User front door. Clicking
      // the beacon only reveals the tooltip -- it never invites anyone.
      id: "invite",
      route: "/admin/users",
      target: "admin-users-add",
      kind: "action",
      section: "Invite an agent",
      title: "Invite an agent",
      body: "Add User invites a new person. Give the beacon a click -- then we will open the invite form together.",
      placement: "left",
    },
    {
      // MODAL step (issue #1342): the engine opens the invite modal for you
      // (openVia clicks the safe, open-only Add User button), waits for the
      // dialog to mount, then highlights it. Nothing is submitted -- close it any
      // time with Esc and the tour carries on. Note the standing outbound-email
      // suppression banner (#1084) explained two steps up still applies to a real
      // invite sent from here.
      id: "invite-modal",
      route: "/admin/users",
      target: "admin-users-add-modal",
      kind: "action",
      openVia: "admin-users-add",
      section: "Invite an agent",
      title: "The invite form",
      body: "This is the invite form: capture the person's name and email and pick their role, and the system sends them a set-password link -- they set their own password on first sign in, you never handle it. When outbound email is switched off for testing, the invite is created but not actually emailed. We have only opened the form to show you; no one is invited while you look.",
      // Center the tooltip so it stays in the viewport ABOVE the open modal (#1342).
      placement: "center",
    },
    {
      id: "roles-presets",
      route: "/admin/users",
      target: "page-admin-users",
      section: "Roles and presets",
      title: "Roles and permission presets",
      body: "Each person is an admin or an agent, and a permission preset decides exactly what they can reach -- a field agent, a paralegal who never sees money, a finance user who does, a manager who signs off deals, and so on. Pick the preset and the workspace shapes itself to the person. That is User Management.",
      placement: "center",
    },
  ],
};

/**
 * Reports -- KPI Reports, Activity Metrics, Agent Usage, Communications,
 * Feedback reports, Payslips. The money report (Payslips) gates on the
 * ``financial`` module.
 */
export const ADMIN_REPORTS_TOUR: TourDefinition = {
  id: "admin-reports",
  title: "Reports tour",
  // Audience gate (issue #1347): admin-shell tour -- offered only to admin-role
  // users, never a plain agent whose ungated first step would otherwise pass.
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/kpis",
      target: "center",
      section: "Welcome",
      title: "Your reports",
      body: "Reports are the numbers you take to a sales meeting -- KPI performance, activity, adoption, communications and payslips. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "kpi-reports",
      route: "/admin/kpis",
      target: "page-admin-kpi-report",
      section: "KPI Reports",
      title: "KPI Reports",
      body: "KPI Reports score every agent on the activity that drives sales -- viewings, valuations and the rest -- company-wide, for any month you choose.",
      placement: "bottom",
    },
    {
      id: "activity",
      route: "/admin/kpis",
      target: "nav-/admin/activity",
      section: "Activity",
      navGroup: "reports",
      title: "Activity Metrics",
      body: "Activity Metrics shows the raw work going in across the agency, so you can see the effort behind the numbers.",
      placement: "right",
    },
    {
      id: "agent-usage",
      route: "/admin/kpis",
      target: "nav-/admin/agent-usage",
      section: "Agent Usage",
      navGroup: "reports",
      title: "Agent Usage",
      body: "Agent Usage tells you who is actually using the system and who has gone quiet, so adoption never slips.",
      placement: "right",
    },
    {
      id: "communications",
      route: "/admin/kpis",
      target: "nav-/admin/communications",
      section: "Communications",
      navGroup: "reports",
      title: "Communications",
      body: "Communications is the company-wide message history and delivery health -- every email and WhatsApp the agency has sent, in one place.",
      placement: "right",
    },
    {
      id: "feedback",
      route: "/admin/kpis",
      target: "nav-/admin/feedback/reports",
      section: "Feedback",
      navGroup: "reports",
      title: "Feedback reports",
      body: "Feedback reports collect every in-app suggestion and bug your team raises, so you can see what they need.",
      placement: "right",
    },
    {
      id: "payslips",
      route: "/admin/kpis",
      target: "nav-/admin/payslips",
      section: "Payslips",
      navGroup: "reports",
      module: "financial",
      title: "Payslips",
      body: "Payslips generate each agent's commission statement on demand, ready to send. That is your reports -- explore at your own pace.",
      placement: "right",
    },
  ],
};

/**
 * Compliance -- FICA dashboard and STR queue, both gated on the ``fica``
 * module. (Commission Authorizations moved to Dashboards, #3820.)
 */
export const ADMIN_COMPLIANCE_TOUR: TourDefinition = {
  id: "admin-compliance",
  title: "Compliance tour",
  // Audience gate (issue #1347): admin-shell tour -- offered only to admin-role
  // users, never a plain agent whose ungated first step would otherwise pass.
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/compliance",
      target: "center",
      section: "Welcome",
      title: "Keeping it clean",
      body: "Compliance keeps the agency on the right side of the rules -- FICA verification and suspicious-transaction reporting. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "fica",
      route: "/admin/compliance",
      target: "page-admin-fica",
      section: "FICA",
      module: "fica",
      title: "FICA Compliance",
      body: "The FICA dashboard tracks verification status, document expiry and high-risk cases across every deal, so nothing registers without the paperwork being right.",
      placement: "bottom",
    },
    {
      id: "str-queue",
      route: "/admin/compliance",
      target: "nav-/admin/str-queue",
      section: "STR Queue",
      navGroup: "compliance",
      module: "fica",
      title: "STR Queue",
      body: "The STR Queue holds the suspicious-transaction reports awaiting your review before they are filed with the regulator.",
      placement: "right",
    },
  ],
};

/**
 * Settings -- My Business, KPI Tiers, Connect your AI, and the ViewSwitcher to
 * step into the agent CRM.
 */
export const ADMIN_SETTINGS_TOUR: TourDefinition = {
  id: "admin-settings",
  title: "Settings tour",
  // Audience gate (issue #1347): admin-shell tour -- offered only to admin-role
  // users, never a plain agent whose ungated first step would otherwise pass.
  audience: { adminOnly: true },
  steps: [
    {
      id: "welcome",
      route: "/admin/business",
      target: "page-admin-business",
      section: "Welcome",
      title: "Your settings",
      body: "Settings is where you shape {agency} to your agency -- your business details, your performance tiers and your AI connection. Here is a quick look. You can leave any time with End tour or the Esc key.",
      placement: "center",
    },
    {
      id: "my-business",
      route: "/admin/business",
      target: "page-admin-business",
      section: "My Business",
      title: "My Business",
      body: "My Business holds your agency's name, contact details, branding and financial year -- the details that flow into your letters, emails and reports. Edit any section any time.",
      placement: "bottom",
    },
    {
      id: "kpi-tiers",
      route: "/admin/business",
      target: "nav-/admin/kpi-tiers",
      section: "KPI Tiers",
      navGroup: "settings",
      title: "KPI Tiers",
      body: "KPI Tiers set each tier's monthly activity-score target and assign your agents, so everyone is measured against the right bar.",
      placement: "right",
    },
    {
      id: "connect-ai",
      route: "/admin/business",
      target: "nav-/admin/connect",
      section: "Connect your AI",
      navGroup: "settings",
      title: "Connect your AI",
      body: "Connect your AI links Claude or ChatGPT to your CRM so you can ask about the agency in plain language. It only ever sees your own data.",
      placement: "right",
    },
    {
      id: "view-switcher",
      route: "/admin/business",
      target: "view-switcher",
      section: "Switch view",
      title: "See what an agent sees",
      body: "Use the view switch up here to step into the agent CRM and see exactly what your agents do day to day. That is Settings -- explore at your own pace.",
      placement: "bottom",
    },
  ],
};

/** Every admin section mini-tour, in nav order. */
export const ADMIN_SECTION_TOURS: TourDefinition[] = [
  ADMIN_DASHBOARDS_TOUR,
  ADMIN_USERS_TOUR,
  ADMIN_REPORTS_TOUR,
  ADMIN_COMPLIANCE_TOUR,
  ADMIN_SETTINGS_TOUR,
];
